import { useEffect, useState } from "react";
import { Stack, Typography, Box, Paper, Button } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import DateRangePicker from "../../components/date-range-picker";
import { FileDownloadOutlined, RefreshOutlined } from "@mui/icons-material";
import { toast } from 'react-hot-toast';
import dayjs from "dayjs";
import { TrendChart, convertToChartData } from "./components";
import { useGetDailyCaseAnalytics } from "../../../../../../features/analytics/hooks/useDailyCaseAnalytics";
import { useExportDailyCaseAnalytics } from "../../../../../../features/analytics/hooks/exports";
import { useAnalyticsSyncDate } from "../../context/analytics-sync-date.context";

export const TrendAnalytics = (props) => {

  const { fromDate: syncedFromDate, toDate: syncedToDate, enableDateSync } = useAnalyticsSyncDate();

  const [fromDate, setFromDate] = useState(() => enableDateSync ? dayjs(syncedFromDate) : dayjs().startOf('month'));
  const [toDate, setToDate] = useState(() => enableDateSync ? dayjs(syncedToDate) : dayjs().endOf('month'));

  useEffect(() => {
    if (!enableDateSync) {
      return;
    }

    if (syncedFromDate) {
      setFromDate(dayjs(syncedFromDate));
    }

    if (syncedToDate) {
      setToDate(dayjs(syncedToDate));
    }
  }, [syncedFromDate, syncedToDate, enableDateSync]);

  useEffect(() => {
    console.log('TrendAnalytics sync date context', {
      fromDate: syncedFromDate,
      toDate: syncedToDate,
      enableDateSync,
    });
  }, [syncedFromDate, syncedToDate, enableDateSync]);

  const handleFromChange = (newDate) => {
    setFromDate(newDate);
  };

  const handleToChange = (newDate) => {
    setToDate(newDate);
  };

  // Fetch real data from backend using the hook
  // This will refetch data whenever the from/to range changes
  // Pass ISO date strings (YYYY-MM-DD) to the hook
  const fromISO = fromDate ? fromDate.format('YYYY-MM-DD') : undefined;
  const toISO = toDate ? toDate.format('YYYY-MM-DD') : undefined;

  const { data: apiData, isFetching, error, refetch } = useGetDailyCaseAnalytics(fromISO, toISO);

  // Export hook with UX callbacks
  const exportDailyCaseAnalytics = useExportDailyCaseAnalytics({
    onSuccess: (data) => {
      toast.success(`Daily case analytics exported successfully! File: ${data.filename}`);
    },
    onError: (error) => {
      toast.error(`Export failed: ${error.message}`);
    }
  });

  // Handle export button click
  const handleExport = () => {
    const exportParams = {
      from: fromISO,
      to: toISO,
    };

    toast.loading('Preparing export...', { id: 'daily-case-analytics-export' });

    exportDailyCaseAnalytics.mutate(exportParams, {
      onSettled: () => {
        toast.dismiss('daily-case-analytics-export');
      }
    });
  };

  // Handle refresh button click
  const handleRefresh = () => {
    refetch();
  };

  // Transform API data to match chart format
  const getChartData = () => {
    if (!apiData?.success || !apiData?.data?.dailyBreakdown) {
      return [];
    }

    const dailyBreakdown = apiData.data.dailyBreakdown;
    const dates = Object.keys(dailyBreakdown).sort();

    // Map API field names to display names
    const fieldMapping = {
      'allocated': 'Allocations',
      'attempted': 'Attempted',
      'connected': 'Connected',
      'unreached': 'Unreached',
      'notInPortal': 'Not in the portal',
      'childAbove2Years': 'Child Above 2 Years',
      'pending': 'Pending',
      'unattempted': 'Unattempted',
      'active': 'Active'
    };

    return Object.entries(fieldMapping).map(([apiField, displayName]) => ({
      id: displayName,
      data: dates.map(date => ({
        x: date,
        y: dailyBreakdown[date][apiField] || 0
      }))
    }));
  };

  const chartData = getChartData();
  const totalDataPoints = apiData?.data?.dailyBreakdown ? Object.keys(apiData.data.dailyBreakdown).length : 0;

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        p: 3,
        border: "2px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: "#fafafa",
        overflowX: "auto",
        mb: 1,
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack spacing={3} sx={{ flexGrow: 1 }}>
          <Typography variant="h4" gutterBottom>
            Trend Analytics
          </Typography>

          <Paper
            elevation={2}
            sx={{ p: 3, mb: 0, border: "1px solid #d0d0d0" }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <DateRangePicker
                fromDate={fromDate}
                toDate={toDate}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                disabled={isFetching || exportDailyCaseAnalytics.isLoading}
                fromLabel="From"
                toLabel="To"
              />

              {/* Refresh and Export Buttons */}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshOutlined />}
                  onClick={handleRefresh}
                  disabled={isFetching || exportDailyCaseAnalytics.isLoading}
                  size="small"
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: '#f3f4f6',
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e',
                    }
                  }}
                >
                  {isFetching ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadOutlined />}
                  onClick={handleExport}
                  disabled={isFetching || exportDailyCaseAnalytics.isLoading || !apiData?.data}
                  size="small"
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: '#f3f4f6',
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e',
                    }
                  }}
                >
                  {exportDailyCaseAnalytics.isLoading ? 'Exporting...' : 'Export'}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Trend Analysis Chart */}
          {isFetching ? (
            <Paper elevation={2} sx={{ p: 3, mb: 2, border: "1px solid #d0d0d0" }}>
              <Typography>Loading trend data...</Typography>
            </Paper>
          ) : error ? (
            <Paper elevation={2} sx={{ p: 3, mb: 2, border: "1px solid #d0d0d0" }}>
              <Typography color="error">Error loading data: {error.message}</Typography>
            </Paper>
          ) : (
            <TrendChart
              data={chartData}
              title={
                fromDate && toDate
                  ? `Daily Trend Analysis - ${fromDate.format("DD MMM YYYY")} — ${toDate.format("DD MMM YYYY")}`
                  : 'Daily Trend Analysis'
              }
              height={500}
            />
          )}

          {(fromDate || toDate) && (
            <Paper elevation={1} sx={{ p: 3, border: "1px solid #d0d0d0" }}>
              <Typography variant="h6" gutterBottom>
                Selected Range Information
              </Typography>
              <Typography variant="body1">
                <strong>From:</strong> {fromDate ? fromDate.format("DD MMMM YYYY") : '—'}
              </Typography>
              <Typography variant="body1">
                <strong>To:</strong> {toDate ? toDate.format("DD MMMM YYYY") : '—'}
              </Typography>
              <Typography variant="body1">
                <strong>Full Range:</strong> {fromDate && toDate ? `${fromDate.format("DD MMM YYYY")} — ${toDate.format("DD MMM YYYY")}` : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isFetching ? (
                  "Loading daily trend data..."
                ) : error ? (
                  "Error loading trend data from backend"
                ) : totalDataPoints > 0 ? (
                  `Displaying daily trend data for ${totalDataPoints} days with 9 categories: 
                  Allocations, Attempted, Connected, Unreached, Not in the portal, Child Above 2 Years, Pending, Unattempted, Active (Backend Data)`
                ) : (
                  "No data available for the selected range"
                )}
              </Typography>
            </Paper>
          )}
        </Stack>
      </LocalizationProvider>
    </Box>
  );
};
